import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import styles from "./StreamSidebar.module.css";

export interface StreamMessage {
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
}

interface StreamSidebarProps {
  messages: StreamMessage[];
  onClose: () => void;
  /** Which view hosts this sidebar — controls the hint copy. Defaults to "outline". */
  context?: "outline" | "draft";
}

export function StreamSidebar({ messages, onClose, context = "outline" }: StreamSidebarProps) {
  const { t } = useTranslation();
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const userMessages = messages.filter((m) => m.role === "user" && m.content.trim().length > 0);

  const handleCopy = useCallback(
    (content: string, idx: number) => {
      navigator.clipboard.writeText(content).then(() => {
        setCopiedIdx(idx);
        toast.success(t("outline.streamCopied"));
        setTimeout(() => setCopiedIdx(null), 2000);
      });
    },
    [t],
  );

  return (
    <aside className={styles.sidebar}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <StreamIcon />
          <h3 className={styles.title}>{t("outline.streamSidebarTitle")}</h3>
          <span className={styles.count}>{userMessages.length}</span>
        </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t("outline.closeStreamSidebar")}
        >
          <CloseIcon />
        </button>
      </header>

      <p className={styles.hint}>
        {context === "draft"
          ? t("draft.streamSidebarHint", "Copy text from your stream to use in your draft.")
          : t("outline.streamSidebarHint")}
      </p>

      <div className={styles.messageList}>
        {userMessages.length === 0 ? (
          <div className={styles.empty}>
            <p>{t("outline.streamSidebarEmpty")}</p>
          </div>
        ) : (
          userMessages.map((msg, idx) => (
            <div key={`${msg.timestamp}-${idx}`} className={styles.messageCard}>
              <p className={styles.messageContent}>{msg.content}</p>
              <button
                type="button"
                className={styles.copyButton}
                onClick={() => handleCopy(msg.content, idx)}
                aria-label={t("outline.copyToClipboard")}
              >
                {copiedIdx === idx ? <CheckIcon /> : <CopyIcon />}
              </button>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}

function StreamIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
      <path
        d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
